import { UserCircle, Buildings, Phone, EnvelopeSimple, FileText, Clock, Globe, ArrowRight, Flag as FlagIcon, UserSwitch } from "@phosphor-icons/react";
import { formatET } from "../../lib/dateUtils";

const TIER_STYLE = {
  HIGH: "text-igc-success border-igc-success/40 bg-igc-success/10",
  MEDIUM: "text-igc-gold border-igc-gold/40 bg-igc-gold/10",
  LOW: "text-igc-danger border-igc-danger/40 bg-igc-danger/10",
};
const TIER_EMOJI = { HIGH: "🟢", MEDIUM: "🟡", LOW: "🔴" };

function formatPagesVisited(pages) {
  if (pages == null) return "—";
  if (Array.isArray(pages)) return `${pages.length} page${pages.length === 1 ? "" : "s"}`;
  return String(pages);
}

function formatTimeOnSite(t) {
  if (t == null) return "—";
  if (typeof t === "number") {
    const m = Math.floor(t / 60), s = Math.round(t % 60);
    return m > 0 ? `${m}m ${s}s` : `${s}s`;
  }
  return String(t);
}

export function WebsiteVisitorCard({ visitor, busy, onConvert, onSkip, onFlag, onReassign }) {
  const tier = visitor.confidence_tier || "LOW";
  const isAutoAssigned = visitor.status === "auto_assigned";
  return (
    <div className="bg-igc-surface border border-igc-gold/15 p-5" data-testid={`website-visitor-card-${visitor.id}`}>
      <div className="flex items-start justify-between gap-4 flex-wrap mb-3">
        <div>
          <p className="text-igc-cream text-sm font-medium flex items-center gap-2">
            <UserCircle size={16} weight="duotone" className="text-igc-muted" /> {visitor.name || "Unidentified Visitor"}
            {visitor.flagged && <FlagIcon size={14} weight="fill" className="text-igc-gold" data-testid={`website-visitor-flagged-icon-${visitor.id}`} title="Flagged for a second look" />}
          </p>
          {visitor.company && <p className="text-igc-muted text-xs mt-1 flex items-center gap-1.5"><Buildings size={12} /> {visitor.company}</p>}
        </div>
        <span className={`text-xs px-2.5 py-1 border uppercase tracking-wide ${TIER_STYLE[tier]}`} data-testid={`website-visitor-confidence-${visitor.id}`}>
          {TIER_EMOJI[tier]} {tier} · {visitor.confidence_score || 0}
        </span>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs text-igc-muted mb-4">
        <p className="flex items-center gap-1.5" data-testid={`website-visitor-phone-${visitor.id}`}><Phone size={12} /> {visitor.phone || "—"}</p>
        <p className="flex items-center gap-1.5" data-testid={`website-visitor-email-${visitor.id}`}><EnvelopeSimple size={12} /> {visitor.email || "—"}</p>
        <p className="flex items-center gap-1.5" data-testid={`website-visitor-pages-${visitor.id}`}><FileText size={12} /> {formatPagesVisited(visitor.pages_visited)}</p>
        <p className="flex items-center gap-1.5" data-testid={`website-visitor-time-on-site-${visitor.id}`}><Clock size={12} /> {formatTimeOnSite(visitor.time_on_site)}</p>
        <p className="flex items-center gap-1.5 col-span-2" data-testid={`website-visitor-traffic-source-${visitor.id}`}><Globe size={12} /> {visitor.traffic_source || "Unknown source"}</p>
      </div>

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-igc-muted text-[11px]" data-testid={`website-visitor-timestamp-${visitor.id}`}>Captured {formatET(visitor.received_at, "MMM d, yyyy h:mm a")} ET</p>
        {isAutoAssigned ? (
          <div className="flex items-center gap-3">
            <span className="text-xs text-igc-success flex items-center gap-1.5" data-testid={`website-visitor-autoassigned-label-${visitor.id}`}>
              <UserSwitch size={14} weight="duotone" /> Auto-assigned to {visitor.assigned_rep_name || "a rep"}
            </span>
            <button data-testid={`website-visitor-reassign-button-${visitor.id}`} disabled={busy} onClick={() => onReassign(visitor)} title="Auto-assign is a smart default, not a lock — send this one to a different rep" className="text-xs px-3 py-1.5 border border-igc-gold/20 text-igc-muted hover:text-igc-goldText hover:border-igc-gold/40 transition-colors disabled:opacity-50">
              Reassign
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <button data-testid={`website-visitor-flag-button-${visitor.id}`} disabled={busy} onClick={() => onFlag(visitor)} title="Keep in queue, flagged for a second look" className="text-xs px-3 py-1.5 border border-igc-gold/20 text-igc-muted hover:text-igc-gold hover:border-igc-gold/40 transition-colors disabled:opacity-50">
              {visitor.flagged ? "Unflag" : "Flag"}
            </button>
            <button data-testid={`website-visitor-skip-button-${visitor.id}`} disabled={busy} onClick={() => onSkip(visitor)} title="Remove from the queue — reversible for 24 hours" className="text-xs px-3 py-1.5 border border-igc-gold/20 text-igc-muted hover:text-igc-danger hover:border-igc-danger/40 transition-colors disabled:opacity-50">
              Skip
            </button>
            <button data-testid={`website-visitor-convert-button-${visitor.id}`} disabled={busy} onClick={() => onConvert(visitor)} title="Turn this visitor into a real lead" className="text-xs px-3 py-1.5 bg-igc-gold text-igc-goldText font-medium hover:bg-igc-gold/90 transition-colors flex items-center gap-1 disabled:opacity-50">
              Convert to Lead <ArrowRight size={12} weight="bold" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
